import ReactTooltip from 'react-tooltip';
import React, { FC, useEffect, useRef, useCallback, useState } from 'react';
import ResizeObserver from 'resize-observer-polyfill';

import RootRef from '@material-ui/core/RootRef';
import { makeStyles } from '@material-ui/core/styles';
import MuiPopover, { PopoverActions, PopoverOrigin } from '@material-ui/core/Popover';

const useStyles = makeStyles({
  root: {
    fontSize: '14px',
  },
});

/** Before using this, check first if `design-system/Dialog` works for your use case (simpler component offering mostly same functionality) */
const Popover: FC<{
  className?: string;
  targetOpen: React.ReactElement;
  targetClosed?: React.ReactElement;
  /** Alias for the children prop */
  popover?: React.ReactNode;
  /**
   * Position value encoding whether the popover will follow a default above or below position.
   * Passing a value for anchorOrigin or transformOrigin will override that aspect of position behavior
   */
  position?: 'above' | 'below';
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  onChange?: (opened: boolean) => void;
  shouldCloseOnClick?: boolean;
  initiallyOpen?: boolean;
  isOpen?: boolean;
  tooltip?: string;
  stopPropagation?: boolean;
  disabled?: boolean;
  width?: number;
  maxHeight?: number;
  style?: React.CSSProperties;
  modifyPaper?: boolean;
  sbk?: boolean; // used in conjunction with a toggle to force close the popover
  children?: React.ReactNode;
}> = props => {
  const {
    popover = props.children,
    className,
    targetOpen,
    targetClosed = targetOpen,
    position,
    anchorOrigin,
    transformOrigin,
    shouldCloseOnClick,
    onChange,
    initiallyOpen,
    isOpen: isPopoverOpen,
    tooltip,
    stopPropagation,
    disabled = false,
    width,
    maxHeight,
    style,
    modifyPaper,
    sbk,
  } = props;
  // set init loaded flag for initiallyOpen
  // initiallyOpen overwrites isPopoverOpen onload, then we consider isPopoverOpen for visibilty
  const [isInitialLoaded, setIsInitialLoad] = useState(false);
  const [isOpen, setIsOpen] = useState(
    !isInitialLoaded && initiallyOpen === false ? false : !!isPopoverOpen,
  );

  const targetRef = useRef<HTMLDivElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<PopoverActions | null>(null);

  const classes = useStyles();

  useEffect(() => {
    if (onChange) {
      onChange(isOpen);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isInitialLoaded && isPopoverOpen !== undefined) {
      setIsOpen(!!isPopoverOpen);
    } else {
      setIsInitialLoad(true);
    }
  }, [isPopoverOpen]);

  // Listen for resize events on the relevant inner element, since Popover will not automatically reposition itself when its contents change
  // Material UI has an open issue to fix: https://github.com/mui-org/material-ui/issues/10595
  useEffect(() => {
    if (rootRef.current) {
      const ro = new ResizeObserver(() => {
        popoverRef.current && popoverRef.current.updatePosition();
      });

      const innerEl = rootRef.current.querySelector('.MuiPopover-paper');

      innerEl && ro.observe(innerEl);
      return () => {
        innerEl && ro.unobserve(innerEl);
      };
    }
  });

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  useEffect(() => {
    handleClose();
  }, [handleClose, sbk]);

  const handleClickPopover = useCallback(
    (e: React.MouseEvent) => {
      if (stopPropagation) {
        e.stopPropagation();
        e.preventDefault();
      }
      if (shouldCloseOnClick) {
        handleClose();
      }
    },
    [handleClose, shouldCloseOnClick, stopPropagation],
  );

  const handleClickTarget = useCallback(
    (e: React.MouseEvent) => {
      if (stopPropagation) {
        e.stopPropagation();
        e.preventDefault();
      }
      !disabled && setIsOpen(true);
    },
    [disabled, stopPropagation, setIsOpen],
  );

  // if you click popover, the tooltip will stay there and won't disappear when you close it
  // so we have to manually hide it
  if (isOpen) {
    ReactTooltip.hide();
  }

  return (
    <React.Fragment>
      <div
        className={`target ${className}`}
        onClick={handleClickTarget}
        ref={targetRef}
        data-tip={tooltip}
        data-for={`toolbar-${tooltip}`}
      >
        {isOpen && targetOpen}
        {!isOpen && targetClosed}
      </div>

      {tooltip && (
        <ReactTooltip id={`toolbar-${tooltip}`} effect="solid">
          {tooltip}
        </ReactTooltip>
      )}

      {isOpen && popover && (
        <RootRef rootRef={rootRef}>
          <MuiPopover
            anchorEl={targetRef.current}
            anchorOrigin={
              anchorOrigin || {
                vertical: position === 'above' ? 'top' : 'bottom',
                horizontal: 'left',
              }
            }
            transformOrigin={
              transformOrigin || {
                vertical: position === 'above' ? 'bottom' : 'top',
                horizontal: 'left',
              }
            }
            open={isOpen}
            onClose={handleClose}
            onClick={handleClickPopover}
            className={classes.root}
            classes={modifyPaper ? { paper: className } : undefined}
            action={popoverRef}
            PaperProps={{ style: { ...style, width, maxHeight } }}
          >
            {popover}
          </MuiPopover>
        </RootRef>
      )}
    </React.Fragment>
  );
};

export default Popover;
